import commonMixin from '../../mixins/_common';
import flashMessageMixin from '../../mixins/_flash_messages';
import loop from '../../components/_loop-component';
import showStaticHtml from '../../components/_show_static_html';
import partsEditer from '../../components/static_html_editor/_parts_editor';
import partsSelecter from '../../components/static_html_editor/_parts_selecter';

/**
 * 静的HTML編集コンポーネント
 */
export default {
    name: 'KanbanStaticHtmlEditor',
    template: '#edit_kanban_template',
    data() {
        return {
            // 編集エリアの切り替えアニメーションを実現するため再レンダリング用フラグ
            show_edit_area: true,
            // マウンテッドフラグ($refsで参照している箇所を表示待ちさせる為)
            mounted: false,
        };
    },
    props: {
        // パーツタイプ build_store|item|custom_page
        type: {
            type: String,
            require: true,
            validator: function (value) {
                return ['build_store', 'item', 'custom_page'].indexOf(value) !== -1;
            }
        },
        // テンプレート
        prop_template: {
            type: Object,
            required: true,
        },
        // 各エリア定義
        areas_config: {
            type: Array,
            default: () => ([]),
        },
        // 編集ID(主に画像参照に使う)
        edit_id: {
            type: [String, Number],
            default: '',
            require: false
        },
        // 編集ブロック
        prop_edit_area: '',
        // 編集データ
        prop_edit_data: {
            type: Object,
            default: () => ({}),
        }
    },
    components: {
        // 静的HTMLを表示するコンポーネント
        'show-static-html': showStaticHtml,
        // 繰り返しコンポーネント
        'loop': loop,
        // パーツ編集コンポーネント
        'parts-editer': partsEditer,
        // 新規パーツ選択コンポーネント
        'parts-selecter': partsSelecter,
    },
    computed: {
        // テンプレート
        template: {
            get() {
                return this.prop_template;
            },
            set(value) {
                this.$emit('update:prop_template', value);
            },
        },
        // 編集ブロック(このコンポーネント用)
        editArea: {
            get() {
                return this.prop_edit_area;
            },
            set(value) {
                this.$emit('update:prop_edit_area', value);
            },
        },
        // 編集データ(このコンポーネント用)
        editData: {
            get() {
                return this.prop_edit_data;
            },
            set(value) {
                this.$emit('update:prop_edit_data', value);
            },
        },
        // 全体設定用データ(編集モーダルへ渡す)
        commonConfig: {
            get() {
                if (!Object.keys(this.editData).length) {
                    return;
                }

                return {
                    background: this.editData.background,
                    font_family: this.editData.font_family,
                    sync_data: this.editData.sync_data,
                    font_color: this.editData.font_color,
                    wrap_align: this.editData.wrap_align,
                };
            },
            set(value) {
                for (let key of Object.keys(value)) {
                    this.editData[key] = value[key];
                }
            },
        },
        // パーツが1つ以上登録されているか
        hasParts: function () {
            return (typeof this.editData.parts !== 'undefined' && this.editData.parts.length > 0);
        },
        // ヘッダーパーツが2個存在するか
        hasHeaderDouble: function () {
            if (typeof this.editData.parts === 'undefined' || this.editData.parts.length < 2) {
                return false;
            }
            // ヘッダーパーツをフィルター
            const headerParts = this.editData.parts.filter((parts) => {
                return parts.key === 'header';
            });
            return headerParts.length > 1;
        },
        // 編集エリアデータリスト
        areasData: function () {
            return this.areas_config.map((area) => {
                area.no_name = area.name;
                return area;
            });
        },
        // プレビュー幅の定義
        areaWidth: function () {
            for (let area of this.areasData) {
                if (area.key === this.editArea) {
                    return area.width;
                }
            }
            return 0;
        },
        // 前の編集エリア
        prevAreaData: function () {
            const last = this.areasData.length - 1;
            for (let i of Object.keys(this.areasData)) {
                if (this.areasData[i].key === this.editArea && i > 0) {
                    const prevIndex = Number(i) - 1;
                    return this.areasData[prevIndex];
                }
            }
            return this.areasData[last];
        },
        // 次の編集エリア
        nextAreaData: function () {
            const last = this.areasData.length - 1;
            for (let i of Object.keys(this.areasData)) {
                if (this.areasData[i].key === this.editArea && i < last) {
                    const nextIndex = Number(i) + 1;
                    return this.areasData[nextIndex];
                }
            }
            return this.areasData[0];
        }
    },
    mixins: [
        commonMixin,
        flashMessageMixin,
    ],
    emits: [
        'update:prop_template',
        'update:prop_edit_area',
        'update:prop_edit_data',
    ],
    // マウンテッドフラグを更新
    beforeCreate: function() {
        this.mounted = false;
    },
    mounted: function() {
        this.mounted = true;
    },
    methods: {
        // パーツを追加する
        addPartsData: function (partsData, index) {
            this.editData.parts.splice(index, 0, partsData);
            this.getStaticHtml();
        },
        // パーツデータ更新
        updatePartsData: function (partsData, index) {
            this.editData.parts[index].data = partsData;
            this.getStaticHtml();
        },
        // パーツを削除する
        removeParts: function (index) {
            this.$refs.loop.remove(index);
            this.getStaticHtml();
        },
        // パーツを選択画面を開く
        openPartsSelecter: function (index) {
            this.$refs.parts_selecter.show(index);
        },
        // パーツ編集画面を開く
        openPartsEditer: function (index) {
            const partsData = _.cloneDeep(this.editData.parts[index]);
            this.$refs.parts_editer.show(partsData, index);
        },
        // editAreaを変更する(アニメーション付き)
        changeEditArea: async function (editArea) {
            this.show_edit_area = false;
            await this.$_sleep(500);
            this.editArea = editArea;
            this.show_edit_area = true;
        },
        // 静的HTML更新
        getStaticHtml: async function () {
            await this.$nextTick();
            // フラッシュメッセージを初期化
            this.clearFlashMessage(null, this.$parent);
            let message = this.exception_error_message;
            this.$_loading();
            try {
                const response = await axios.post('/get_static_html', {
                    _method: 'POST',
                    edit_id: this.edit_id,
                    type: this.type,
                    template: this.template,
                    device_sync: false,
                });

                // 静的HTMLを更新
                this.template = response.data.template;
                // メッセージは空にする
                message = '';
            } catch (e) {
                message = this.getCatchMessage(e);
            }
            this.$_unloading();
            if (message) {
                this.appendFlashMessage({
                    level: 'alert-danger',
                    message: message,
                }, this.$parent);
            }
        }
    }
};
